import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui';
import Badge from '@/components/ui/Badge';
import { formatDateTime, formatStatus, getStatusColor } from '@/lib/utils';
import type { HoldRequest } from '@/types';

interface RecentActivityProps {
  requests: HoldRequest[];
  isLoading?: boolean;
}

export function RecentActivity({ requests, isLoading }: RecentActivityProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Your latest hold requests</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-neutral-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-neutral-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (requests.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Your latest hold requests</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-neutral-400 text-center py-8">No recent activity</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
        <CardDescription>Your latest hold requests</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {requests.map((request) => (
            <Link
              key={request.id}
              to={`/holds/${request.id}`}
              className="block p-4 border border-neutral-200 rounded-lg hover:border-primary-500 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-neutral-900">{request.requestNumber}</h3>
                    {/* OPE-386 (c): workflowStatus='cancelled' overrides derivedStatus — handled by getStatusColor/formatStatus */}
                    <Badge className={getStatusColor(request.derivedStatus, request.workflowStatus)}>
                      {formatStatus(request.derivedStatus, request.workflowStatus)}
                    </Badge>
                  </div>
                  <p className="text-sm text-neutral-600 mb-2">{request.advertiser}</p>
                  <div className="flex items-center gap-4 text-xs text-neutral-400">
                    <span>
                      {request.totalLineItems} item{request.totalLineItems !== 1 ? 's' : ''}
                    </span>
                    <span>•</span>
                    <span>{request.standardHoldsCount} standard</span>
                    <span>•</span>
                    <span>{request.exceptionHoldsCount} exception</span>
                  </div>
                </div>
                <div className="text-xs text-neutral-400 ml-4">
                  {formatDateTime(request.updatedAt)}
                </div>
              </div>
            </Link>
          ))}
        </div>
        {requests.length > 0 && (
          <div className="mt-4 text-center">
            <Link
              to="/holds"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View all requests →
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
